import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { NAVY, SUBTLE, PRIMARY_BLUE, SUCCESS, BORDER } from '../../../styles/foundation/colors';

const shopifySteps = [
  { label: 'Login to Shopify', description: 'Login to your Shopify Account and go to your Store > Apps', image: '/img/Shopify/img1.png' },
  { label: 'Search Proship', description: 'Go to Shopify App Store and search "Proship"', image: '/img/Shopify/img2.png' },
  { label: 'Add App', description: 'Click on "Add App"', image: '/img/Shopify/img3.png' },
  { label: 'Install', description: 'Click "Install"', image: '/img/Shopify/img4.png' },
  { label: 'Add Credentials', description: 'Add your Proship Credentials in the configuration screen', image: '/img/Shopify/img5.png' },
  { label: 'Start Processing', description: 'Your Proship account will get Active on Shopify. Now you may start processing your Orders from Shopify.', image: '/img/Shopify/img6.png' },
];

const woocommerceSteps = [
  { label: 'Go to Plugins', description: 'Login to your WooCommerce Admin Panel and go to Plugins > Add New > Upload', image: '/img/WooCommerce/img (1).png' },
  { label: 'Upload Plugin', description: 'Click on "Add New" and upload the Proship plugin shared with you', image: '/img/WooCommerce/img (2).png' },
  { label: 'Install & Activate', description: 'Install the plugin and activate it successfully', image: '/img/WooCommerce/img (3).png' },
  { label: 'Open Proship Settings', description: 'Go to Settings > WooCommerce > Proship to configure the plugin', image: '/img/WooCommerce/img (4).png' },
  { label: 'Complete Setup', description: 'List products, set up taxes, define shipping zones, and add payment methods from the Settings page' },
  { label: 'Start Creating Orders', description: 'Begin placing orders from your WooCommerce store', image: '/img/WooCommerce/img (5).png' },
  { label: 'Order Sync', description: "Once the order status becomes 'Processing', it will appear in Proship's 'Channel Orders' page", image: '/img/WooCommerce/img (6).png' },
];

const magentoSteps = [
  { label: 'Install Proship Module', description: 'Install the Proship module in your Magento environment', image: '/img/Magento/img (1).png' },
  { label: 'Open Configuration', description: 'Go to Stores > Settings > Configuration from the Magento Admin Panel', image: '/img/Magento/img (2).png' },
  { label: 'Access Proship Settings', description: 'Find your custom Proship tab/module on the Configuration page', image: '/img/Magento/img (3).png' },
  { label: 'Enter Credentials', description: 'Add your Proship Username and Auth Password in the Configuration', image: '/img/Magento/img (4).png' },
  { label: 'Add Merchant Details', description: 'Enter Merchant ID and select your desired Payment Methods', image: '/img/Magento/img (5).png' },
  { label: 'Enter Additional Info', description: 'Add Pending Order Date, Magento Admin Username, and Password. Fill all fields before proceeding', image: '/img/Magento/img (6).png' },
  { label: 'Save Configuration', description: 'Once all details are filled correctly, click on "Save Config"', image: '/img/Magento/img (7).png' },
  { label: 'Login / Create Proship Account', description: 'Login or create your Proship account if not already done', image: '/img/Magento/img (8).png' },
  { label: 'Place an Order', description: 'Add products to cart and place an order from your Magento store', image: '/img/Magento/img (9).png' },
  { label: 'Go to Sales > Orders', description: 'After placing the order, go to the Sales > Orders section to view details', image: '/img/Magento/img (10).png' },
  { label: 'Order Synchronization', description: 'Once synced, Proship Order ID will be displayed in the order grid table', image: '/img/Magento/img (11).png' },
  { label: 'Shipment Sync & Status Update', description: 'After shipment is processed, the order status will be updated under Sales > Shipment', image: '/img/Magento/img (12).png' },
];

const platformSteps = { '1': shopifySteps, '2': woocommerceSteps, '3': magentoSteps };

const ChannelOrderHelp = ({ open, onClose }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [platform, setPlatform] = useState('1');
  const [previewImage, setPreviewImage] = useState(null);

  const steps = platformSteps[platform] || [];
  const step = steps[activeStep];
  const isComplete = activeStep >= steps.length;

  const handlePlatformChange = (_e, val) => {
    setPlatform(val);
    setActiveStep(0);
  };

  const handleReset = () => setActiveStep(0);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}>
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: `1px solid ${BORDER}`, py: 1.5, px: 2.5,
        }}>
          <Typography sx={{ fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.semibold, color: NAVY }}>
            Store Configuration Guide
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Platform tabs */}
          <TabContext value={platform}>
            <Box sx={{ borderBottom: `1px solid ${BORDER}` }}>
              <TabList onChange={handlePlatformChange} centered
                sx={{
                  minHeight: 40,
                  '& .MuiTab-root': {
                    fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.medium,
                    textTransform: 'none', minHeight: 40, py: 0,
                  },
                }}>
                <Tab label="Shopify" value="1" />
                <Tab label="WooCommerce" value="2" />
                <Tab label="Magento" value="3" />
              </TabList>
            </Box>

            {['1', '2', '3'].map((key) => (
              <TabPanel key={key} value={key} sx={{ p: 0 }}>
                {/* Step indicator chips */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, px: 2.5, py: 1.5, borderBottom: `1px solid ${BORDER}`, bgcolor: '#fafafa' }}>
                  {(platformSteps[key] || []).map((s, i) => (
                    <Chip
                      key={i}
                      label={`${i + 1}. ${s.label}`}
                      size="small"
                      onClick={() => setActiveStep(i)}
                      sx={{
                        fontFamily: FONT, fontSize: 11, fontWeight: WEIGHT.medium, height: 26, cursor: 'pointer',
                        bgcolor: i === activeStep ? PRIMARY_BLUE : i < activeStep ? SUCCESS : '#fff',
                        color: i <= activeStep ? '#fff' : NAVY,
                        border: i > activeStep ? `1px solid ${BORDER}` : 'none',
                        '&:hover': { opacity: 0.85 },
                      }}
                    />
                  ))}
                </Box>
              </TabPanel>
            ))}
          </TabContext>

          {/* Step content */}
          {!isComplete && step ? (
            <Box sx={{ px: 2.5, py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip label={`Step ${activeStep + 1} of ${steps.length}`} size="small"
                  sx={{ fontFamily: FONT, fontSize: 11, fontWeight: WEIGHT.semibold, height: 24, bgcolor: '#e3f2fd', color: PRIMARY_BLUE }} />
                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.semibold, color: NAVY }}>
                  {step.label}
                </Typography>
              </Box>

              <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mb: 1.5 }}>
                {step.description}
              </Typography>

              {step.image && (
                <Box
                  onClick={() => setPreviewImage(step.image)}
                  sx={{
                    cursor: 'pointer', borderRadius: 1, overflow: 'hidden',
                    border: `1px solid ${BORDER}`, mb: 1.5,
                    '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
                  }}
                >
                  <img src={step.image} alt={step.label}
                    style={{ width: '100%', maxHeight: 360, objectFit: 'contain', display: 'block', background: '#fafafa' }} />
                </Box>
              )}

              {/* Nav buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Button size="small" disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)}
                  startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
                  sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none' }}>
                  Back
                </Button>
                <Button size="small" variant="contained" disableElevation
                  onClick={() => setActiveStep((s) => s + 1)}
                  endIcon={activeStep < steps.length - 1 ? <ArrowForwardIcon sx={{ fontSize: 14 }} /> : <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
                  sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none' }}>
                  {activeStep < steps.length - 1 ? 'Next' : 'Finish'}
                </Button>
              </Box>
            </Box>
          ) : isComplete && (
            <Box sx={{ px: 2.5, py: 4, textAlign: 'center' }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 48, color: SUCCESS, mb: 1 }} />
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.semibold, color: NAVY, mb: 0.5 }}>
                All steps completed
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mb: 2 }}>
                Your store integration guide is complete.
              </Typography>
              <Button size="small" variant="outlined" onClick={handleReset}
                startIcon={<RestartAltIcon sx={{ fontSize: 14 }} />}
                sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none' }}>
                Restart Guide
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Image preview dialog */}
      <Dialog open={Boolean(previewImage)} onClose={() => setPreviewImage(null)} maxWidth="xl"
        PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', position: 'relative' } }}>
        <IconButton onClick={() => setPreviewImage(null)} size="small"
          sx={{ position: 'absolute', right: -36, top: -36, color: '#fff', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
        {previewImage && (
          <img src={previewImage} alt="Enlarged view" style={{ maxHeight: '90vh', maxWidth: '90vw', objectFit: 'contain' }} />
        )}
      </Dialog>
    </>
  );
};

export default ChannelOrderHelp;
